import styled from 'styled-components';

// shared
import CorePrimaryButton from 'shared/button/CorePrimaryButton';

// components
import LoginInput from '../../components/login/LoginInput';
import { Body, BodyFooter, BodyTitle, InputForm } from './style';
import { KeyboardEventHandler, useRef } from 'react';

// hooks
import usePasswordCheck from 'hooks/login/usePasswordCheck';
import usePasswordDoubleCheck from 'hooks/login/usePasswordDoubleCheck';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useSetPasswordEmailMutation } from 'hooks/mutations/user';
import useToast from 'hooks/useToast';

const PasswordReset = () => {
	// router
	const navigate = useNavigate();
	const params = useParams();
	const [search_params] = useSearchParams();
	const email_query = search_params.get('email');

	const toast = useToast();

	// ref
	const pwd_ref = useRef<HTMLInputElement>(null);
	const pwd_double_ref = useRef<HTMLInputElement>(null);
	const btn_ref = useRef<HTMLButtonElement>(null);

	// hooks
	const [
		password,
		password_check,
		password_helper_text,
		passwordStateCheck,
		passwordChange,
	] = usePasswordCheck(pwd_ref);

	const [
		password_double,
		double_check,
		double_helper_text,
		doubleStateCheck,
		doubleChange,
	] = usePasswordDoubleCheck(pwd_double_ref, password);

	const { mutateAsync: pwdMutate, isLoading: pwd_loading } =
		useSetPasswordEmailMutation();

	const fetchPassword = async () => {
		if (
			!password ||
			!pwd_double_ref.current ||
			!params.uid ||
			!params.token ||
			!email_query
		)
			return;
		try {
			const data = {
				email: email_query,
				uid: params.uid,
				token: params.token,
				new_password1: password,
				new_password2: pwd_double_ref.current.value,
			};

			await pwdMutate(data, {
				onSuccess() {
					navigate(`/imms/login`, { replace: true });
					toast('비밀번호가 변경되었습니다. 변경된 비밀번호로 로그인 해주세요.');
				},
				onError() {
					toast('비밀번호 변경을 실패했습니다.', false, true);
				},
			});
		} catch (err) {
			console.log(err);
		}
	};

	const clickNext = () => {
		passwordStateCheck(password);
		doubleStateCheck(pwd_double_ref.current?.value);

		if (password_check !== 'pass') {
			pwd_ref.current?.focus();
		} else if (double_check !== 'pass') {
			pwd_double_ref.current?.focus();
		}

		if (password_check !== 'pass' || double_check !== 'pass') return;

		fetchPassword();
	};

	const enterEvent: KeyboardEventHandler<HTMLInputElement> = (e) => {
		if (e.key !== 'Enter' || btn_ref.current === null) return;

		btn_ref.current.click();
	};

	return (
		<Body>
			<BodyTitle>비밀번호 재설정</BodyTitle>

			<InputPasswordForm>
				<LoginInput
					type="password"
					_placeholder="새 비밀번호"
					input_id="reset-new-pwd"
					_value={password}
					_onChange={passwordChange}
					is_error={password_check !== 'none' && password_check !== 'pass'}
					helper={password_helper_text[password_check]}
					_ref={pwd_ref}
					_onKeyDown={enterEvent}
				/>
				<LoginInput
					type="password"
					_placeholder="새 비밀번호 확인"
					input_id="reset-new-pwd-check"
					_value={password_double}
					_onChange={doubleChange}
					is_error={double_check !== 'none' && double_check !== 'pass'}
					helper={double_helper_text[double_check]}
					_ref={pwd_double_ref}
					_onKeyDown={enterEvent}
				/>
			</InputPasswordForm>

			<BodyFooter>
				<CorePrimaryButton
					size="large"
					style_type="filled"
					text="변경하기"
					_onClick={clickNext}
					is_loading={pwd_loading}
					_ref={btn_ref}
				/>
			</BodyFooter>
		</Body>
	);
};

const InputPasswordForm = styled(InputForm)`
	padding-bottom: 16px;
`;

export default PasswordReset;
